import { FC, ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Lock, LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

import { buttonVariants } from './ui/button';

interface Link {
  title: string;
  to?: string;
  label?: ReactNode;
  icon?: LucideIcon;
  subLinks?: Link[];
  blank?: boolean;
  disabled?: boolean;
  new?: boolean;
  locked?: boolean;
  requiresYearlyPlan?: boolean;
  onLockedClick?: () => void;
}
interface Props {
  links: Link[];
  disabled?: boolean;
}

export const Nav: FC<Props> = ({ links, disabled }) => {
  const { pathname } = useLocation();

  return (
    <nav>
      <ul className="grid gap-1">
        {links.map((link) => (
          <li key={link.title}>
            {link.subLinks ? (
              <>
                <NavLink
                  to={link.subLinks[0].to!}
                  className={cn(
                    'flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-medium text-muted-foreground',
                    link.subLinks.some((subLink) =>
                      pathname.includes(subLink.to ?? '/')
                    ) && 'mb-2 bg-card text-primary shadow',
                    (link.disabled || disabled) &&
                      'pointer-events-none cursor-not-allowed'
                  )}
                >
                  {link.icon && <link.icon size={16} strokeWidth={2} />}
                  {link.title}
                </NavLink>
                <ul
                  className={cn(
                    'pl-4 sm:hidden',
                    link.subLinks.some((subLink) =>
                      pathname.includes(subLink.to ?? '/')
                    ) && 'sm:block'
                  )}
                >
                  {link.subLinks.map((subLink) => (
                    <li key={subLink.title}>
                      <NavLink
                        to={subLink.to ?? '/'}
                        target={link.blank ? '_blank' : '_self'}
                        className={({ isActive }) =>
                          cn(
                            buttonVariants({
                              variant: 'ghost',
                              size: 'sm',
                            }),
                            'flex justify-start gap-2 rounded-none border-l-[1.5px] pl-2 transition-all hover:bg-transparent',
                            isActive &&
                              'active group border-primary text-primary hover:border-primary hover:text-primary',
                            (subLink.disabled || disabled) &&
                              'pointer-events-none cursor-not-allowed'
                          )
                        }
                      >
                        {subLink.title}
                        {subLink.label && !disabled && (
                          <span className="ml-auto block rounded-md bg-success-foreground/5 p-1 text-xs text-success-foreground group-[.active]:bg-primary/5 group-[.active]:text-primary">
                            {subLink.label}
                          </span>
                        )}
                        {subLink.new && (
                          <span className="ml-auto block rounded-md bg-yellow-500/10 p-1 text-xs text-yellow-500">
                            New
                          </span>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </>
            ) : link.locked ? (
              <button
                onClick={link.onLockedClick}
                className={cn(
                  buttonVariants({
                    variant: 'ghost',
                    size: 'sm',
                  }),
                  'flex w-full justify-start gap-2 opacity-60 hover:opacity-80'
                )}
              >
                {link.icon && <link.icon size={16} strokeWidth={2} />}
                {link.title}
                <span className="ml-auto flex items-center gap-1">
                  {link.new && (
                    <span className="rounded-md bg-yellow-500/10 p-1 text-xs text-yellow-500">
                      New
                    </span>
                  )}
                  <span className="rounded-md bg-primary/5 p-1">
                    <Lock size={10} strokeWidth={3} className="text-primary" />
                  </span>
                </span>
              </button>
            ) : (
              <NavLink
                to={link.to ?? '/'}
                target={link.blank ? '_blank' : '_self'}
                className={({ isActive }) =>
                  cn(
                    buttonVariants({
                      variant: 'ghost',
                      size: 'sm',
                    }),
                    'flex justify-start gap-2 ',
                    isActive &&
                      'bg-card text-primary  shadow  hover:bg-card hover:text-primary',
                    (link.disabled || disabled) &&
                      'pointer-events-none cursor-not-allowed'
                  )
                }
              >
                {link.icon && <link.icon size={16} strokeWidth={2} />}
                {link.title}
                {link.label && <span className="ml-auto  ">{link.label}</span>}
                {link.new && (
                  <span className="ml-auto rounded-md bg-yellow-500/10 p-1 text-xs text-yellow-500">
                    New
                  </span>
                )}
              </NavLink>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};
